import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface FinalCTAProps {
  headline: string;
  description: string;
  primaryCTA: { text: string };
  onPrimaryCTAClick?: () => void;
}

/**
 * FinalCTA (Neo-Brutalist Version)
 *
 * Bottom-of-page call-to-action section with:
 * - Geometric background shapes
 * - Card container with hard shadow
 * - Two CTA buttons
 * - Entrance animations
 */
export function FinalCTA({
  headline,
  description,
  primaryCTA,
  onPrimaryCTAClick,
}: FinalCTAProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32 relative overflow-hidden bg-[#FFFBF0]"
    >
      {/* Decorative geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={cn(
            "absolute top-10 left-10 w-24 h-24",
            "bg-[#FFDC00] rounded-2xl",
            "border-3 border-black",
            "rotate-12 opacity-30"
          )}
        />
        <div
          className={cn(
            "absolute bottom-20 right-20 w-32 h-32",
            "bg-[#FF73A9] rounded-full",
            "border-3 border-black",
            "opacity-30"
          )}
        />
        <div
          className={cn(
            "absolute top-1/2 left-1/4 w-16 h-16",
            "bg-[#3F5EF4] rounded-xl",
            "border-3 border-black",
            "-rotate-6 opacity-25"
          )}
        />
        <div
          className={cn(
            "absolute bottom-10 left-1/3 w-20 h-20",
            "bg-[#00EB90] rounded-2xl",
            "border-3 border-black",
            "rotate-12 opacity-25"
          )}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          className={cn(
            "max-w-3xl mx-auto text-center",
            "bg-white rounded-3xl",
            "border-3 border-black",
            "shadow-[8px_8px_0_0_#000000]",
            "p-8 lg:p-12",
            "transition-all duration-500 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          {/* Headline */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6 font-['Epilogue'] text-[#1A1A1A]">
            {headline}
          </h2>

          {/* Description */}
          <p className="text-[#666666] text-base lg:text-lg mb-10 max-w-xl mx-auto font-['Sora']">
            {description}
          </p>

          {/* CTAs */}
          <div
            className={cn(
              "flex flex-col sm:flex-row items-center justify-center gap-4",
              "transition-all duration-300 ease-out",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
          >
            {onPrimaryCTAClick ? (
              <Button size="lg" onClick={onPrimaryCTAClick}>
                {primaryCTA.text}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Link to="/signup">
                <Button size="lg">
                  {primaryCTA.text}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
